import React, { useRef, useState, useCallback, useContext, useEffect, useLayoutEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import gsap from 'gsap';

import bgVideo from './../assets/videos/menskunde_bg.mp4';
import setFullscreenVideoAspectRatio from '../helpers/VideoHelper';

import { MenskundeContext } from '../components/MenskundeLayoutWrapper';
import { UserContext } from '../App';
import CardSelectModal from '../components/CardSelectModal';

function PageMenskunde() {
    const userContext = useContext(UserContext);
    const { topics } = useContext(MenskundeContext);

    const navigate = useNavigate();

    const [activeHoverTitle, setActiveHoverTitle] = useState('Laden..');

    const topicMenuEl = useRef(null);
    const videoEl = useRef(null);
    const cloudsEl = useRef(null);

    const [showTopicsList, setShowTopicsList] = useState(false);

    const [showCardSelectModal, setShowCardSelectModal] = useState(false);
    const [cardSelectCards, setCardSelectCards] = useState([]);
    const [cardSelectTopics, setCardSelectTopics] = useState([]);

    const handleOutsideTopicMenuClick = useCallback(e => {
        if (topicMenuEl.current && !topicMenuEl.current.contains(e.target)) {
            setShowTopicsList(false);
        }
    }, []);

    function keyUpEvent(e) {
        if (e.key.toLowerCase() === 'escape') {
            setShowTopicsList(false);
        }
    }

    function onTopicClick(topic) {
        if (topic.cards.length === 0 && topic.subtopics.length === 0) return;

        setCardSelectCards(topic.subtopics.length === 0 ? topic.cards : []);
        setCardSelectTopics(topic.subtopics.length > 0 ? topic.subtopics : []);
        setShowCardSelectModal(true);
    }

    function resize() {
        if (videoEl.current === null) return;
        setFullscreenVideoAspectRatio(videoEl.current, 16 / 9);
    }

    function setActiveHoverTitleForTopic(topic) {
        if (topic.cards.length === 0 && topic.subtopics.length === 0) setActiveHoverTitle('');
        else setActiveHoverTitle(topic.name);
    }

    resize();

    useLayoutEffect(() => {
        if (videoEl.current === null) return;

        resize();
        window.addEventListener('resize', resize);

        const interval = setInterval(() => {
            if (videoEl.current.currentTime > 6) {
                videoEl.current.currentTime = 2.3;
            }
        }, 100);

        return () => {
            clearInterval(interval);
            window.removeEventListener('resize', resize);
        };
    }, []);

    useEffect(() => {
        window.addEventListener('keyup', keyUpEvent);
        return () => window.removeEventListener('keyup', keyUpEvent);
    }, [userContext.isLoggedIn, navigate]);

    useEffect(() => {
        document.addEventListener('mouseup', handleOutsideTopicMenuClick);
        return () => document.removeEventListener('mouseup', handleOutsideTopicMenuClick);
    }, [handleOutsideTopicMenuClick]);

    useEffect(() => {
        if (topics.length === 0) return;

        gsap.set(cloudsEl.current, { opacity: 0 });

        videoEl.current.play();
        setActiveHoverTitle('');

        const context = gsap.context(() => {
            gsap.timeline({ delay: 0.5 })
                .set('.jsCloud', { yPercent: -100 })
                .set(cloudsEl.current, { opacity: 1 })
                .to('.jsCloud', { duration: 0.5, opacity: 1, ease: 'power2.out' })
                .to('.jsCloud', { duration: 2, yPercent: 0, ease: 'power2.out' }, '-=0.5')
                .set('.jsCloud', { clearProps: 'yPercent' });
        }, cloudsEl);

        return () => context.revert();
    }, [topics]);

    return (<>
        <div className="menskunde-topics">
            {!!topics.length &&
                <div className={'menskunde-topics__topic-menu topic-menu' + (showTopicsList ? ' open' : '')} ref={topicMenuEl}>
                    <div className="top-spacer"></div>

                    <div className="topic-menu__inner">
                        <button className="topic-menu__btn" type="button" onClick={() => { setShowTopicsList(!showTopicsList) }}>Topics</button>

                        <ul className="topic-menu__list">
                            <li className="title">Alle onderwerpen</li>

                            {topics.filter(topic => topic.cards.length > 0 || topic.subtopics.length > 0).map((topic, index) => {
                                return <li className={'heading:3' + (index === 0 ? ' first' : '')} key={`topic-${index}`}><Link to={'/menskunde/topics/' + topic.slug}>{topic.name}</Link></li>;
                            })}
                        </ul>
                    </div>
                </div>
            }

            <div className={'menskunde-topics__clouds' + (topics.length ? ' visible' : '')} ref={cloudsEl}>
                {topics.map((topic, index) => {
                    return (
                        <div key={`topic-${index}`} className={'menskunde-topics__cloud jsCloud topic-' + (index + 1) + (activeHoverTitle === topic.name ? ' active' : '')}>
                            <button type="button" onClick={() => onTopicClick(topic)} onMouseOver={() => setActiveHoverTitleForTopic(topic)}>{topic.name}</button>
                        </div>
                    );
                })}

                <span className="menskunde-topics__hover-title">{activeHoverTitle}</span>
            </div>

            <div className="menskunde-topics__bg-wrapper">
                <video ref={videoEl} src={bgVideo} type="video/mp4" playsInline muted></video>
            </div>

            <CardSelectModal show={showCardSelectModal} setShow={setShowCardSelectModal} cards={cardSelectCards} subtopics={cardSelectTopics} />
        </div>
    </>);
}

export default PageMenskunde;